import { IPhotoEntityReference } from '../basic';
import { EntityReference } from '../basic/basic';
import { ICurrency, IMethod } from '../structure';

export interface IPaymentMethod {
  /** Id of the payment method */
  paymentMethodID: number;
  /** name for the payment method */
  name?: string;
  /** description of the payment method */
  description?: string;
  /** additional hint of the payment method (e.g. bank account data) */
  hint?: string;
  /** picture of the payment method */
  picture?: IPhotoEntityReference;
  /** index of the payment method */
  index: number;
}

export interface IVoucherCode {
  voucherCodeID: number;
  usedOn: Date;
  keyValue: string;
  eAN: string;
  voucher: EntityReference;
}

export interface IPayment {
  paymentID: number;
  reservedUntil: Date;
  info: string;
  price: number;
  currency: ICurrency;
  voucher: EntityReference;
  voucherCode: IVoucherCode;
  paymentMethod: IPaymentMethod;
  debitCard: EntityReference;
  chainStore: EntityReference;
  paymentServiceProviderData: string;
}

export interface IPaymentInitData {
  /** The programatic-name of the provider */
  provider: PaymentProvider;
  /** The transaction-token to use for the provider */
  transactionToken: string;
  /** Optional redirect-url to use after payment */
  redirectUrl?: string;
}

/** The types of payment-providers (which are also fully implemented) */
export type PaymentProvider = 'DataTransServiceProvider' | 'PayPalCheckoutProvider2' | 'StripeServiceProvider';

/** Possible statuses for a payment */
export enum PaymentStatus {
  Pending = 0,
  Cancelled = 1,
  Failed = 2,
  Succeeded = 3
}

export interface IPaymentStatus {
  /** Status of the payment */
  status: PaymentStatus;
  /** The transactionId given by the payment-provider to identify the payment */
  transactionId: string;
  /** Optional message to display (different per provider, not always used) */
  message?: string;
}

export interface IPaymentResultMethods {
  /** Calling api-endpoint if the payment was a success */
  paymentSuccess?: IMethod;
  /** Calling api-endpoint if the payment was canceled */
  paymentCancel?: IMethod;
  /** Calling api-endpoint if the payment has failed */
  paymentError?: IMethod;
  /** Calling api-endpoint to check if the payment is confirmed */
  paymentStatus?: IMethod;
}

export interface IPaymentErrorResult<T> {
  /** Error message from the payment_service used (can be different strings per provider) */
  error: T;
}

export interface IPaymentSuccessResult {
  methods: IPaymentSuccessMethods;
}

export type IPaymentConfirmationResult =
  | IPaymentConfirmationSuccessResult
  | IPaymentConfirmationCompletedResult
  | IPaymentConfirmationPendingResult;

interface IPaymentConfirmationBaseResult {
  /** The current status of the payment */
  paymentStatus: PaymentStatus;
}
interface IPaymentConfirmationPendingResult extends IPaymentConfirmationBaseResult {
  /** Whether the payment has been confirmed */
  paymentStatus: PaymentStatus.Pending;
}
interface IPaymentConfirmationCompletedResult extends IPaymentConfirmationBaseResult {
  /** Whether the payment has been confirmed */
  paymentStatus: PaymentStatus.Cancelled | PaymentStatus.Failed;
  /** Optional redirect URL after completion */
  redirectUrl?: string;
}
interface IPaymentConfirmationSuccessResult extends IPaymentConfirmationBaseResult {
  /** Whether the payment has been confirmed */
  paymentStatus: PaymentStatus.Succeeded;
  /** Optional redirect URL after completion */
  redirectUrl?: string;
  /** Methods to execute after confirmation */
  methods: IPaymentSuccessMethods;
}

export interface IPaymentSuccessMethods {
  executeOrder: IMethod;
}

/** Possible query-statuses for a payment (url-query) */
type PaymentStatusQuery = 'succeeded' | 'error' | 'cancelled' | 'confirmation';

/** Map to convert query-status to PaymentStatus */
export const PaymentStatusQueryMap: { [key in PaymentStatusQuery]: PaymentStatus } = {
  succeeded: PaymentStatus.Succeeded,
  error: PaymentStatus.Failed,
  cancelled: PaymentStatus.Cancelled,
  confirmation: PaymentStatus.Pending
};

/** Shared base query-parameters for a payment redirect */
export interface IPaymentBaseQueryParams {
  /** Status of the payment */
  state: PaymentStatusQuery;
}
